import {
  Collapse,
  RotatingChevron,
  constants,
  useWindowSize,
} from "@newsela/angelou";
import { actionNames, actionPrefixes, objectTypes } from "@newsela/snitch";
import PropTypes from "prop-types";
import { Fragment, useEffect } from "react";

import ExportablesModal from "@/components/AIComponents/ExportablesModal";
import useHasFeature from "@/features/useHasFeature";
import canViewGradingPage from "@/grading/utils/canViewGradingPage";
import { ASSESSMENT_TYPE } from "@/types/models/ContentAttachment.interface";
import useAppcues from "@/utils/useAppcues";
import ActivityPickerModal from "@/view/ArticleActivityPicker/ActivityPickerModal";
import activityTypes from "@/view/constants/activityTypes";
import assessmentTypes from "@/view/constants/assessmentTypes";
import focusElementRefKeys from "@/view/constants/focusElementRefKeys";
import { GUIDED_HIGHLIGHTING_ACTIVITY_PANEL } from "@/view/GuidedHighlightingContainer/utils/utils";
import ViewableActivity from "@/view/TeacherActivityContainer/ViewableActivity/ViewableActivity.tsx";
import getActivityPanelNavigationHref from "@/view/utils/getActivityPanelNavigationHref";
import getActivitySubjectName from "@/view/utils/getActivitySubjectName";
import getChecksForUnderstandingQuizAttachment from "@/view/utils/getChecksForUnderstandingQuizAttachment";
import getEventPropertiesContentType from "@/view/utils/getEventPropertiesContentType";
import getGuidedHighlightingQuizAttachment from "@/view/utils/getGuidedHighlightingQuizAttachment";
import useActivityPanel from "@/view/utils/useActivityPanel";
import useAttachmentsContext from "@/view/utils/useAttachmentsContext";
import useChecksForUnderstandingVisibility from "@/view/utils/useChecksForUnderstandingVisibility";
import useFocusManager from "@/view/utils/useFocusManager/useFocusManager";
import useGuidedHighLightingTeacher from "@/view/utils/useGuidedHighLightingTeacher";
import usePollContext from "@/view/utils/usePollContext";
import usePresentModeContext from "@/view/utils/usePresentModeContext";
import useReadingSkillsContext from "@/view/utils/useReadingSkillsContext";
import SectionHeader from "../SectionHeader";
import {
  collapseBtnCss,
  elaTitleCss,
  headerSectionCss,
} from "./ActivitiesListContainer.style";

const propTypes = {
  assignments: PropTypes.array,
  assignmentsLoading: PropTypes.bool,
  attachments: PropTypes.object,
  content: PropTypes.object,
  contentId: PropTypes.string,
  contentMetadata: PropTypes.arrayOf(PropTypes.object),
  currentLevel: PropTypes.object,
  dispatch: PropTypes.func,
  contentType: PropTypes.string,
  legacyArticleLevelHeaderId: PropTypes.number,
  levelId: PropTypes.string,
  router: PropTypes.object,
  setVisibleActivityIds: PropTypes.func,
  updatedAssignment: PropTypes.object,
  userProperties: PropTypes.object,
  visibleActivityIds: PropTypes.array,
};

const headerText = "Activities";
const { POWER_WORDS_ACTIVITY, QUIZ, WRITE_PROMPT, GUIDED_HIGHLIGHTING_QUIZ } =
  assessmentTypes;

const ActivitiesListContainer = ({
  assignments,
  assignmentsLoading,
  attachments,
  content,
  contentId,
  contentMetadata,
  currentLevel,
  dispatch,
  contentType,
  legacyArticleLevelHeaderId,
  levelId,
  router,
  setVisibleActivityIds,
  updatedAssignment,
  userProperties,
  visibleActivityIds,
}) => {
  const { setIsActivityPanelExpanded } = useActivityPanel();
  const appcues = useAppcues();
  const { width: windowWidth } = useWindowSize();
  const {
    breakpoints: {
      Breakpoints: { sm },
    },
  } = constants;
  const isMobile = windowWidth <= sm;
  const { focusManagerRefs, setFocusManagerRef } = useFocusManager();
  const { readingSkillState } = useReadingSkillsContext();
  const pollContainerRef = focusManagerRefs?.current?.pollContainer;
  const checkForUnderstandingQuizQuestionRef =
    focusManagerRefs?.current?.checkForUnderstandingQuizQuestion;

  const isAuthorizedGradingPageUser = canViewGradingPage(userProperties);
  const { isPresentModeActive } = usePresentModeContext();

  const { isPollSelected, setIsPollSelected } = usePollContext();
  const { attachments: activityAttachments, setCurrentActivityCount } =
    useAttachmentsContext();

  const hasChecksForUnderstandingQuiz =
    !!getChecksForUnderstandingQuizAttachment(activityAttachments);

  const [canViewGHFeatureEnabled] = useHasFeature(
    "can_view_guided_highlighting",
  );

  const guidedHighlightingQuizAttachment =
    getGuidedHighlightingQuizAttachment(activityAttachments);

  const hasGuidedHighlightingFeature =
    guidedHighlightingQuizAttachment && canViewGHFeatureEnabled;

  useEffect(() => {
    if (hasChecksForUnderstandingQuiz) {
      // Send event to notify Appcues to launch the Checks for Understanding Flow
      const APP_CUES_EVENT =
        "Teacher visited Checks for Understanding eligible article";

      return appcues?.track(APP_CUES_EVENT);
    }
  }, [hasChecksForUnderstandingQuiz]);

  // Toggling Checks for Understanding Visibility
  const { isChecksForUnderstandingVisible, toggleChecksForUnderstanding } =
    useChecksForUnderstandingVisibility(userProperties);

  // Toggling Guided Highlighting Visibility
  const { isTeacherGuidedHighlightingActive, toggleTeacherGuidedHighlighting } =
    useGuidedHighLightingTeacher(
      hasGuidedHighlightingFeature,
      userProperties,
      updatedAssignment,
    );

  const { activeELALicense, canViewPowerWords, isStudent } = userProperties;
  const { pathname, query } = router;
  const selectedActivityIds =
    updatedAssignment?.activity_members?.map((member) => member.content_id) ||
    [];
  const selectedActivitiesCount =
    Object.values(attachments)
      .flat()
      .filter((attachment) => selectedActivityIds?.includes(attachment.id))
      .length || 0;

  const updateHref = (href) => {
    return router.replace(href, null, { shallow: true });
  };

  useEffect(() => {
    setCurrentActivityCount(selectedActivitiesCount);
  }, [selectedActivitiesCount]);

  const handleClickViewableActivity = (attachment, isElaSkillsQuiz = false) => {
    const href = getActivityPanelNavigationHref({
      attachment,
      isStudent,
      pathname,
      query,
      isElaSkillsQuiz,
    });
    updateHref(href);
  };

  const getAnnotationsSection = () => {
    const href = {
      pathname,
      query: {
        ...query,
        activityPanel: "annotations",
      },
    };

    const eventProperties = {
      objectType: objectTypes.annotation,
      actionPrefix: actionPrefixes.initiate,
      actionName: actionNames.navigate,
      streamProperties: {
        content_type: "Article",
        description: "View annotations activity panel",
        content_id: contentId,
      },
    };

    return (
      <ViewableActivity
        assessmentType={ASSESSMENT_TYPE.ANNOTATIONS}
        buttonDataProps={{ "data-qa-selector": "view-annotations" }}
        eventProperties={eventProperties}
        isSelected
        isViewableOnly
        label="Annotations"
        onClick={() => updateHref(href)}
        showViewButton
      />
    );
  };

  const getPollActivitySection = () => {
    const eventProperties = {
      objectType: objectTypes.poll,
      actionPrefix: actionPrefixes.initiate,
      actionName: actionNames.navigate,
      streamProperties: {
        content_type: getEventPropertiesContentType(contentType),
        content_id: levelId,
        description: "View Student Poll",
      },
    };

    const pollCheckboxDataProps = {
      "data-qa-selector": `${isPollSelected ? "remove" : "add"}-poll-${isPollSelected ? "from" : "to"}-assignment`,
    };

    return (
      <ViewableActivity
        assessmentType={ASSESSMENT_TYPE.POLL}
        buttonDataProps={{ "data-qa-selector": "view-or-edit-poll-activity" }}
        checkboxDataProps={pollCheckboxDataProps}
        dispatch={dispatch}
        eventProperties={eventProperties}
        isSelected={isPollSelected}
        label="Student Poll"
        onClick={() => {
          pollContainerRef?.focus({ preventScroll: true });
          pollContainerRef?.scrollIntoView({ behavior: "smooth" });
        }}
        setIsPollSelected={setIsPollSelected}
        showViewButton={isPollSelected}
      />
    );
  };

  const getChecksForUnderstandingSection = () => {
    const eventProperties = {
      objectType: "ChecksForUnderstanding",
      actionPrefix: actionPrefixes.initiate,
      actionName: actionNames.navigate,
      streamProperties: {
        content_type: getEventPropertiesContentType(contentType),
        content_id: levelId,
        description: "View Checks for Understanding",
      },
    };

    const checksForUnderstandingDataProps = {
      "data-qa-selector": `${isChecksForUnderstandingVisible ? "remove" : "add"}-checks-for-understanding-quiz-activity-${isChecksForUnderstandingVisible ? "from" : "to"}-assignment`,
    };

    if (!hasChecksForUnderstandingQuiz) return null;

    return (
      <>
        <ViewableActivity
          assessmentType={ASSESSMENT_TYPE.CHECKS_FOR_UNDERSTANDING_QUIZ}
          buttonDataProps={{
            "data-qa-selector": "view-checks-for-understanding",
          }}
          checkboxDataProps={checksForUnderstandingDataProps}
          dispatch={dispatch}
          eventProperties={eventProperties}
          isSelected={isChecksForUnderstandingVisible}
          label="Checks for Understanding"
          toggleChecksForUnderstanding={toggleChecksForUnderstanding}
          onClick={() => {
            if (isMobile) {
              setIsActivityPanelExpanded(false);
            }
            setTimeout(() => {
              checkForUnderstandingQuizQuestionRef?.scrollIntoView({
                behavior: "smooth",
              });
            });
          }}
          showViewButton={isChecksForUnderstandingVisible}
        />
      </>
    );
  };

  const getGuidedHighlightingSection = () => {
    const href = {
      pathname,
      query: {
        ...query,
        activityPanel: GUIDED_HIGHLIGHTING_ACTIVITY_PANEL,
      },
    };
    const guidedHighlightingDataProps = {
      "data-qa-selector": `${isTeacherGuidedHighlightingActive ? "remove" : "add"}-guided-highlighting-activity-${isTeacherGuidedHighlightingActive ? "from" : "to"}-assignment`,
    };

    const eventProperties = {
      objectType: "GuidedHighlighting",
      actionPrefix: actionPrefixes.initiate,
      actionName: actionNames.navigate,
      streamProperties: {
        content_type: getEventPropertiesContentType(contentType),
        content_id: levelId,
        description: "View Guided Highlighting",
      },
    };

    if (!hasGuidedHighlightingFeature) return null;

    return (
      <ViewableActivity
        buttonDataProps={{
          "data-qa-selector": "view-guided-highlighting",
        }}
        checkboxDataProps={guidedHighlightingDataProps}
        assessmentType={ASSESSMENT_TYPE.GUIDED_HIGHLIGHTING_QUIZ}
        dispatch={dispatch}
        eventProperties={eventProperties}
        isSelected={isTeacherGuidedHighlightingActive}
        label="Guided Highlighting"
        onClick={() => updateHref(href)}
        showBetaTag={false}
        showViewButton={isTeacherGuidedHighlightingActive}
        toggleGuidedHighlighting={toggleTeacherGuidedHighlighting}
      />
    );
  };

  const getGraphicOrganizersSection = () => {
    return (
      <ExportablesModal.Teacher
        content={content}
        contentMetadata={contentMetadata}
        currentLevel={currentLevel}
        dispatch={dispatch}
        levelId={levelId}
        setVisibleActivityIds={setVisibleActivityIds}
        visibleActivityIds={visibleActivityIds}
      />
    );
  };

  const getActivitiesSection = () => {
    return Object.entries(attachments).map(([subject, activities]) => {
      // The guided highlighting quiz is addressed in a separated section.
      const activitiesWithoutGuidedHighlighting = activities.filter(
        (activity) => activity.content_type !== GUIDED_HIGHLIGHTING_QUIZ.type,
      );

      const filteredActivities = canViewPowerWords
        ? activitiesWithoutGuidedHighlighting
        : activitiesWithoutGuidedHighlighting.filter(
            (activity) => activity.content_type !== POWER_WORDS_ACTIVITY.type,
          );
      return (
        <Fragment key={subject}>
          <h3 css={elaTitleCss}>
            {getActivitySubjectName(subject, activeELALicense)}
          </h3>
          {filteredActivities.map((attachment, index) => {
            const assessmentType = attachment.data.assessment_type;
            const activityType = activityTypes[assessmentType];
            const canEdit =
              (activeELALicense &&
                subject === "ELA" &&
                assessmentType !== POWER_WORDS_ACTIVITY.type) ||
              assessmentType === WRITE_PROMPT.type;
            const isElaSkillsQuiz =
              activeELALicense && assessmentType === QUIZ.type;
            const isSelected = selectedActivityIds.includes(attachment.id);
            const checkboxDataProps = {
              "data-qa-selector": `${isSelected ? "remove" : "add"}-${
                activityType?.objectType
              }-activity-${isSelected ? "from" : "to"}-assignment`,
            };
            const buttonDataProps = {
              "data-qa-selector": `view${canEdit ? "-or-edit" : ""}-${
                activityType?.objectType
              }-activity`,
            };

            const selectedReadingSkills = Object.values(
              readingSkillState,
            ).filter(({ isReadingSkillSelected }) => isReadingSkillSelected);

            const label = isElaSkillsQuiz
              ? `${activityType?.label}: ${
                  selectedReadingSkills.length
                } reading skill${selectedReadingSkills.length === 1 ? "" : "s"}`
              : activityType?.label;

            const eventProperties = {
              objectType: activityType?.objectType,
              actionPrefix: "Initiate",
              actionName: "Navigate",
              streamProperties: {
                content_type: "Article",
                description: `View ${activityType?.objectType} activity panel`,
                content_id: levelId,
                object_id: attachment.id,
              },
            };

            return (
              <ViewableActivity
                attachment={attachment}
                buttonDataProps={buttonDataProps}
                checkboxDataProps={checkboxDataProps}
                dispatch={dispatch}
                eventProperties={eventProperties}
                isElaSkillsQuiz={isElaSkillsQuiz}
                isSelected={isSelected}
                key={index}
                label={label}
                onClick={() =>
                  handleClickViewableActivity(attachment, isElaSkillsQuiz)
                }
                setVisibleActivityIds={setVisibleActivityIds}
                visibleActivityIds={visibleActivityIds}
              />
            );
          })}
        </Fragment>
      );
    });
  };

  const getReviewSection = () => {
    const canSeeReviewSection =
      isAuthorizedGradingPageUser && !isPresentModeActive;

    if (!canSeeReviewSection) return null;

    const eventProperties = {
      objectType: objectTypes.content,
      actionPrefix: actionPrefixes.initiate,
      actionName: actionNames.navigate,
      streamProperties: {
        content_type: "Article",
        description: "View review student work modal",
        content_id: contentId,
      },
    };

    return (
      <ActivityPickerModal
        eventProperties={eventProperties}
        assignments={assignments}
        assignmentsLoading={assignmentsLoading}
        contentId={contentId}
        isElaReadingSkillsStyle
        legacyArticleLevelHeaderId={legacyArticleLevelHeaderId}
        levelId={levelId}
      />
    );
  };

  return (
    <Collapse
      initialIsHidden={false}
      __cssFor={collapseBtnCss}
      renderButtonContents={(isHidden, timeout) => (
        <div css={headerSectionCss}>
          <SectionHeader
            headerText={headerText}
            ref={(ref) => {
              setFocusManagerRef(
                ref,
                focusElementRefKeys.activitiesListContainerHeading,
              );
            }}
            tabIndex="-1"
          />

          <RotatingChevron isDown={isHidden} timeout={timeout} />
        </div>
      )}
    >
      {getAnnotationsSection()}
      {getPollActivitySection()}
      {getChecksForUnderstandingSection()}
      {getGuidedHighlightingSection()}
      {getGraphicOrganizersSection()}
      {getActivitiesSection()}
      {getReviewSection()}
    </Collapse>
  );
};

ActivitiesListContainer.propTypes = propTypes;
export default ActivitiesListContainer;
